import { getGclid } from '../analytics/gclid'

/**
 * Agendamiento Cal.com embebido en modal de marca.
 * - Lazy-on-intent: el embed.js se carga al primer hover/focus de un CTA.
 * - Evento de conversión: `diagnostico_agendado` — MISMO nombre y payload
 *   que el sistema validado E2E del WP (2026-04-27); GTM ya lo conoce.
 * - Tras agendar: redirect a /gracias/ (conversión medible por URL).
 */
const CAL_LINK = 'carlos-morandi-jalapenos/diagnostico-gratuito'
const CAL_NS = 'jalapenos'

type CalFn = ((...args: unknown[]) => void) & {
  q?: unknown[]
  ns?: Record<string, CalFn>
  loaded?: boolean
}

declare global {
  interface Window {
    Cal?: CalFn
  }
}

let calBooted = false
let modal: HTMLDivElement | null = null

export function initCalModal(): void {
  const links = [...document.querySelectorAll<HTMLAnchorElement>('a[data-cta]')].filter((a) =>
    a.href.includes('/agendar'),
  )
  for (const a of links) {
    a.addEventListener('pointerenter', bootCal, { once: true })
    a.addEventListener('focus', bootCal, { once: true })
    a.addEventListener('click', (ev) => {
      ev.preventDefault()
      openModal()
    })
  }
}

/** Snippet oficial de Cal.com (idéntico al del WP, con namespace). */
function bootCal(): void {
  if (calBooted) return
  calBooted = true

  const w = window
  const p = (api: CalFn, args: IArguments | unknown[]) => {
    api.q!.push(args)
  }
  if (!w.Cal) {
    const cal: CalFn = function (...args: unknown[]) {
      if (!cal.loaded) {
        cal.ns = {}
        cal.q = cal.q ?? []
        const s = document.createElement('script')
        s.src = 'https://app.cal.com/embed/embed.js'
        document.head.appendChild(s)
        cal.loaded = true
      }
      if (args[0] === 'init') {
        const api: CalFn = function (...inner: unknown[]) {
          p(api, inner)
        }
        const namespace = args[1]
        api.q = api.q ?? []
        if (typeof namespace === 'string') {
          cal.ns![namespace] = cal.ns![namespace] ?? api
          p(cal.ns![namespace]!, args)
          p(cal, ['init', api])
        } else {
          p(cal, args)
        }
        return
      }
      p(cal, args)
    }
    w.Cal = cal
  }

  w.Cal('init', CAL_NS, { origin: 'https://app.cal.com' })

  const ns = () => w.Cal!.ns![CAL_NS]!

  ns()('ui', {
    theme: document.documentElement.dataset.theme === 'tinta' ? 'light' : 'dark',
    styles: { branding: { brandColor: '#CCFF00' } },
    hideEventTypeDetails: false,
    layout: 'month_view',
  })

  ns()('on', {
    action: 'bookingSuccessful',
    callback: (eventData: unknown) => {
      let attendeeEmail: string | null = null
      try {
        const detail = (eventData as { detail?: { data?: { booking?: { attendees?: { email?: string }[] } } } }).detail
        attendeeEmail = detail?.data?.booking?.attendees?.[0]?.email ?? null
      } catch {
        /* payload inesperado */
      }
      window.dataLayer ??= []
      window.dataLayer.push({
        event: 'diagnostico_agendado',
        source: 'cal_com',
        gclid: getGclid(),
        email: attendeeEmail,
        timestamp: new Date().toISOString(),
      })
      setTimeout(() => {
        // Absoluta: la relativa './gracias.html' desde una URL limpia interna
        // (/contacto/) resolvía a /contacto/gracias.html -> 404 tras convertir
        location.href = '/gracias/'
      }, 1400)
    },
  })
}

function openModal(): void {
  bootCal()
  if (!modal) {
    modal = document.createElement('div')
    modal.className = 'cal-modal'
    modal.innerHTML = `
      <div class="cal-modal__backdrop" data-close></div>
      <div class="cal-modal__sheet" role="dialog" aria-modal="true" aria-label="Agenda tu diagnóstico gratuito">
        <button type="button" class="cal-modal__close mono" data-close aria-label="Cerrar">CERRAR ×</button>
        <div id="cal-booking-widget"></div>
      </div>
    `
    document.body.appendChild(modal)
    modal.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('[data-close]')) closeModal()
    })
    addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal()
    })
    window.Cal!.ns![CAL_NS]!('inline', {
      elementOrSelector: '#cal-booking-widget',
      config: { layout: 'month_view' },
      calLink: CAL_LINK,
    })
  }
  modal.hidden = false
  document.body.style.overflow = 'hidden'
  window.dataLayer ??= []
  window.dataLayer.push({ event: 'cal_modal_open' })
}

function closeModal(): void {
  if (!modal) return
  modal.hidden = true
  document.body.style.overflow = ''
}
